// Una carpetita con fotos que asoman y se abren en abanico al pasar el mouse — el
// `ImagesBadge` de Aceternity que pasó Matías, portado a este repo
// (ADR-2026-10-02-redes-carpeta-en-los-botones): sin `motion` (no está en el proyecto;
// las transiciones son CSS con una curva con rebote) y sin color ámbar fijo (`tono`
// azul o violeta). La abre quien la monta (`abierta`), para que responda al hover de la
// tarjeta entera y no sólo al de la carpeta.

import React from 'react';

export interface CarpetaDeImagenesProps {
  /** Hasta tres; se recortan. */
  imagenes: string[];
  /**
   * Sin imágenes, UNA tarjeta blanca con esto adentro (un ícono): es lo que lleva "Añadir
   * referencia" mientras no hay nada subido. Con imágenes no se usa.
   */
  relleno?: React.ReactNode;
  tono: 'azul' | 'celeste' | 'cian' | 'violeta' | 'gris' | 'coral';
  /** Abierta: las fotos salen y se abren en abanico; cerrada: asoman apenas. */
  abierta: boolean;
  /** Medidas de la carpeta (px). */
  ancho?: number;
  alto?: number;
  className?: string;
}

const TONOS: Record<CarpetaDeImagenesProps['tono'], { atras: string; frente: string; linea: string; solapa: string }> = {
  gris: {
    atras: 'linear-gradient(to bottom, #9AA1B4, #7F879C)',
    frente: 'linear-gradient(to bottom, #B8BECB, #9AA1B4)',
    solapa: 'linear-gradient(to bottom, #C6CBD6, #ABB1C0)',
    linea: 'rgba(255,255,255,0.4)',
  },
  azul: {
    atras: 'linear-gradient(to bottom, #2E5BFF, #1644EB)',
    frente: 'linear-gradient(to bottom, #5B7DFF, #2E5BFF)',
    solapa: 'linear-gradient(to bottom, #6F8DFF, #4B6DFF)',
    linea: 'rgba(255,255,255,0.35)',
  },
  // Los azules de Crear (ADR-2026-10-09-asistente-en-crear): el modelo pasa de violeta a
  // celeste y la referencia de gris a cian, todo en la familia de la marca.
  celeste: {
    atras: 'linear-gradient(to bottom, #4FA0FF, #2F8BFF)',
    frente: 'linear-gradient(to bottom, #8CC4FF, #5AA6FF)',
    solapa: 'linear-gradient(to bottom, #A6D2FF, #79B7FF)',
    linea: 'rgba(255,255,255,0.4)',
  },
  cian: {
    atras: 'linear-gradient(to bottom, #3CC7DE, #1BA9C9)',
    frente: 'linear-gradient(to bottom, #8BE9F3, #4FD3E6)',
    solapa: 'linear-gradient(to bottom, #A9F0F7, #6FDDEC)',
    linea: 'rgba(255,255,255,0.45)',
  },
  violeta: {
    atras: 'linear-gradient(to bottom, #B9A5F5, #A08BEA)',
    frente: 'linear-gradient(to bottom, #D2C4FA, #B9A5F5)',
    solapa: 'linear-gradient(to bottom, #DCD1FB, #C7B8F7)',
    linea: 'rgba(255,255,255,0.45)',
  },
  // La tarjeta "Modo" de Crear (ADR-2026-10-09-modos-adentro-de-crear): cálida, para que no
  // se confunda con las tres de adjuntos.
  coral: {
    atras: 'linear-gradient(to bottom, #FF7A59, #F2553A)',
    frente: 'linear-gradient(to bottom, #FF9C7A, #FF7A59)',
    solapa: 'linear-gradient(to bottom, #FFB096, #FF8C6B)',
    linea: 'rgba(255,255,255,0.4)',
  },
};

/** La curva con un poco de rebote, en lugar del spring de `motion`. */
const CURVA = 'cubic-bezier(0.34, 1.4, 0.64, 1)';

export const CarpetaDeImagenes: React.FC<CarpetaDeImagenesProps> = ({
  imagenes,
  relleno,
  tono,
  abierta,
  ancho = 40,
  alto = 30,
  className = '',
}) => {
  const t = TONOS[tono];
  // Sin fotos, el relleno ocupa el lugar de una: misma tarjeta, con el nodo adentro.
  const lista: (string | null)[] = imagenes.length > 0 ? imagenes.slice(0, 3) : relleno ? [null] : [];
  const n = lista.length;
  const anchoSolapa = ancho * 0.375;
  const altoSolapa = alto * 0.25;
  // Medidas de las fotos: asomando y abiertas (proporcionales a la carpeta).
  const foto = abierta ? { w: ancho * 0.9, h: alto * 0.8 } : { w: ancho * 0.62, h: alto * 0.58 };
  const subida = -alto * 0.87;
  const separacion = ancho * 0.37;
  const giro = 15;

  return (
    <span
      aria-hidden="true"
      className={`relative block shrink-0 ${className}`}
      style={{ width: ancho, height: alto, perspective: 1000, transformStyle: 'preserve-3d' }}
    >
      {/* Atrás de la carpeta, con su solapa. */}
      <span className="absolute inset-0 rounded-[4px] shadow-sm" style={{ background: t.atras }}>
        <span
          className="absolute left-0.5 rounded-t-[2px]"
          style={{ top: -altoSolapa * 0.65, width: anchoSolapa, height: altoSolapa, background: t.solapa }}
        />
      </span>

      {/* Las fotos que salen. */}
      {lista.map((src, i) => {
        const centro = n === 1 ? 0 : n === 2 ? i - 0.5 : i - 1;
        const x = abierta ? centro * separacion : 0;
        const y = abierta ? subida - (n - 1 - i) * 3 : -4 - (n - 1 - i);
        const rot = abierta ? centro * giro : centro * 3;
        return (
          <span
            key={`${src ?? 'relleno'}:${i}`}
            className="absolute left-1/2 top-0.5 grid origin-bottom place-items-center overflow-hidden rounded-[3px] bg-white text-[#02062B99] shadow-sm ring-1 ring-black/10"
            style={{
              width: foto.w,
              height: foto.h,
              zIndex: 10 + i,
              transform: `translateX(calc(-50% + ${x}px)) translateY(${y}px) rotate(${rot}deg)`,
              transition: `transform 420ms ${CURVA} ${i * 30}ms, width 420ms ${CURVA} ${i * 30}ms, height 420ms ${CURVA} ${i * 30}ms`,
            }}
          >
            {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : relleno}
          </span>
        );
      })}

      {/* El frente, que se abate al abrir. */}
      <span
        className="absolute inset-x-0 bottom-0 origin-bottom rounded-[4px] shadow-sm"
        style={{
          height: '85%',
          background: t.frente,
          zIndex: 20,
          transformStyle: 'preserve-3d',
          transform: `rotateX(${abierta ? -45 : -25}deg) scaleY(${abierta ? 0.8 : 1})`,
          transition: `transform 420ms ${CURVA}`,
        }}
      >
        <span className="absolute left-1 right-1 top-1 h-px" style={{ background: t.linea }} />
      </span>
    </span>
  );
};

export default CarpetaDeImagenes;
